import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { BogIndicator } from 'src/domains/sportsbook/webview/components/raceIndicator/BogIndicator';
import { RaceIndicatorsWrapper } from 'src/domains/sportsbook/webview/components/raceIndicator/RaceIdicator.style';
import { ExtraPlacesIndicator } from 'src/domains/sportsbook/webview/components/raceIndicator/ExtraPlacesIndicator';
import { RaceSummaryLoader } from 'src/domains/sportsbook/webview/components/raceCard/raceCardLoader/RaceSummaryLoader';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { HorseRacingSortOrderType } from 'src/domains/sportsbook/webview/components/raceCard/RaceCompetition.state';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { RaceFiltersState } from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/RaceFiiters.state';
import { RaceBannerOrFilters } from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/TableMessageOrFilters';
import { RaceCardHeader } from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/raceCardHeader/RaceCardHeader';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { RaceDetailsWrapper } from './RaceDetails.style';
import { RaceResultsGh } from 'src/domains/sportsbook/webview/components/raceCard/raceResults/greyhoundRacing/RaceResultsGh';
import { RaceResultsHr } from 'src/domains/sportsbook/webview/components/raceCard/raceResults/horseRacing/RaceResultsHr';
import { HorseRacingRow } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRow/HorseRacing/HorseRacingRow';
import { sortSelections } from 'src/domains/layouts/utils/sortSelections';
import {
    getActiveFilterContent,
    getFilters,
} from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/RaceFilters.utils';
import { RaceDetailsState } from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/RaceDetails.state';
import {
    PriceBoostListWrapper,
    RaceSummaryCaptionHeader,
} from 'src/domains/sportsbook/webview/components/raceSummary/raceSummarySport/horseRacing/HeadersHorseRacing.style';
import { TableHeaderCell } from 'src/ui/components/table/sports/row/hr-gr/event/custom/TableHeaderCell';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';

interface RaceDetailsPropsType {
    eventId: EventId;
    sport?: 'horseracing' | 'greyhoundracing';
    sortOrder: HorseRacingSortOrderType;
    setSortOrder: (sort: HorseRacingSortOrderType) => void;
}

export const RaceDetails = observer('RaceDetails', (props: RaceDetailsPropsType) => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);
    const { basicData } = BasicDataModel.get(common);
    const configState = ConfigComponents.get(common).config;
    const { showHorseRacingSortDropdown } = configState;
    const { eventId, sport, sortOrder, setSortOrder } = props;

    const shouldShowHorseRacingDropdown = showHorseRacingSortDropdown === true && sport === 'horseracing';
    const [filterStrategy] = useState(() => new RaceFiltersState(languagesState));
    const [state] = useState(() => new RaceDetailsState(common, eventId));

    if (state.isLoading) {
        return <RaceSummaryLoader />;
    }

    const race = state.race;
    if (race === null) {
        return null;
    }

    const filters = getFilters(filterStrategy, race, sortOrder);
    const selectFilter = getActiveFilterContent(filterStrategy, filters);

    const isWinOrEwActive = selectFilter?.key === 'Win or Ew';

    const winOrEwMarket = race.marketRaceWinnerAll.find((market) => market.display === true);
    const winOrEwMarketModel = winOrEwMarket === undefined ? null : winOrEwMarket.id2.getModel();

    const shouldShowPriceBoostRows = isWinOrEwActive === true && winOrEwMarketModel !== null;

    const visibleSelections =
        winOrEwMarketModel === null
            ? []
            : sortSelections(winOrEwMarketModel.selections).filter((selection) => selection.display === true);

    const boostedSelections =
        shouldShowPriceBoostRows === true
            ? visibleSelections.filter((selection) => selection.isBoostedPriceVisible)
            : [];
    const shouldRenderBoostedSelections = configState.isBoostedPriceAvailable && boostedSelections.length > 0;

    const bogValue = basicData.valueReady?.bpEnabled ?? false;
    const shouldShowBogForMarket = selectFilter?.showBogForMarket ?? false;

    const bogIndicator =
        bogValue === true && race.raceHasBp === true && shouldShowBogForMarket ? <BogIndicator /> : null;

    const bogCard = (
        <RaceIndicatorsWrapper>
            {bogIndicator}
            <ExtraPlacesIndicator event={race} />
        </RaceIndicatorsWrapper>
    );

    if (state.isFinishedRace && sport !== undefined) {
        return (
            <div key={race.id2.toOldId()}>
                <RaceCardHeader
                    bogCard={bogCard}
                    hasStream={state.hasStream}
                    isFinishedRace={state.isFinishedRace}
                    isRaceInProgress={state.isRaceInProgress}
                    race={race}
                    shouldShowBogForMarket={shouldShowBogForMarket}
                    strategy={filterStrategy}
                />

                {sport === 'horseracing' ? <RaceResultsHr race={race} /> : <RaceResultsGh race={race} />}
            </div>
        );
    }

    return (
        <div key={race.id2.toOldId()}>
            <RaceCardHeader
                bogCard={bogCard}
                hasStream={state.hasStream}
                isFinishedRace={state.isFinishedRace}
                isRaceInProgress={state.isRaceInProgress}
                race={race}
                shouldShowBogForMarket={shouldShowBogForMarket}
                strategy={filterStrategy}
            />
            <RaceDetailsWrapper variant={configState.componentVariants.sportsbookVariant}>
                {shouldRenderBoostedSelections &&
                    boostedSelections.map((selection, index) => {
                        return (
                            <PriceBoostListWrapper key={`price-boost-row-${selection.id2.key}`}>
                                {index === 0 && (
                                    <RaceSummaryCaptionHeader variant={configState.componentVariants.sportsbookVariant}>
                                        <TableHeaderCell isWide={true}>
                                            <I18n
                                                langKey='events.race-summary.price-boost.label'
                                                defaultText='Price Boost'
                                            />
                                        </TableHeaderCell>

                                        <TableHeaderCell
                                            align='center'
                                            mobileSize='48px'
                                            tabletSize='63px'
                                        >
                                            <I18n
                                                langKey='events.race-summary.price-was.label'
                                                defaultText='Was'
                                            />
                                        </TableHeaderCell>

                                        <TableHeaderCell
                                            align='center'
                                            mobileSize='48px'
                                            tabletSize='63px'
                                        >
                                            <I18n
                                                langKey='events.race-summary.price-now.label'
                                                defaultText='Now'
                                            />
                                        </TableHeaderCell>
                                    </RaceSummaryCaptionHeader>
                                )}
                                <HorseRacingRow
                                    key={`price-boost-${selection.id2.key}`}
                                    selectionId={selection.id2}
                                    isRaceInProgress={state.isRaceInProgress}
                                    isPriceHistoryHidden={true}
                                    isPriceBoostActive={true}
                                />
                            </PriceBoostListWrapper>
                        );
                    })}

                <RaceBannerOrFilters
                    isRaceInProgress={state.isRaceInProgress}
                    activeFilterId={selectFilter?.key ?? null}
                    filters={filters}
                    shouldShowHorseRacingDropdown={shouldShowHorseRacingDropdown}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder}
                />
                {selectFilter?.content}
            </RaceDetailsWrapper>
            <PriceChangeLegend isRace={true} />
        </div>
    );
});
